import { EOrderStatus } from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { load } from '../reducers/product/reducer';
import { formatDateTime, formatPrice } from '../utils/browser-format';
import StatusBadge from './status-badge';

export interface IOrderDetailViewAddress {
  name: string;
  surname: string;
  companyName?: string;
  street: string;
  streetNumber?: string;
  city: string;
  zip: string;
  countryLabel: React.JSX.Element | null;
  email?: string;
}

export interface IOrderDetailViewProduct {
  uuid: string;
  name: string;
  link: string;
  count: number;
  pricePerUnit: number;
  lineTotal: number;
}

export interface IOrderDetailViewProps {
  uuid: string;
  createdAt?: string;
  orderStatusCode: string;
  currencyIsoCode: string;
  companyHasPricesWithoutTax: boolean;
  totalPrice: number;
  deliveryFee?: number;
  hasInvoice?: boolean;
  invoiceUrl?: string;
  products: IOrderDetailViewProduct[];
  billingAddress?: IOrderDetailViewAddress;
  deliveryAddress?: IOrderDetailViewAddress | null;
  paymentMethodName?: string;
  deliveryMethodName?: string;
  backUrl?: string;
  backLabel?: string;
  dispatch: Dispatch;
  onPayNow?: () => void;
  isRetryingPayment?: boolean;
  paymentRetryError?: boolean;
  onCancelOrder?: () => void;
  isCancellingOrder?: boolean;
  orderCancelError?: boolean;
}

export default function OrderDetailView({
  uuid,
  createdAt,
  orderStatusCode,
  currencyIsoCode,
  companyHasPricesWithoutTax,
  totalPrice,
  deliveryFee,
  hasInvoice,
  invoiceUrl,
  products,
  billingAddress,
  deliveryAddress,
  paymentMethodName,
  deliveryMethodName,
  backUrl,
  backLabel,
  dispatch,
  onPayNow,
  isRetryingPayment,
  paymentRetryError,
  onCancelOrder,
  isCancellingOrder,
  orderCancelError,
}: IOrderDetailViewProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="account-order-detail">
      <div className="account-order-detail__header">
        <div>
          <h1 className="account-order-detail__title">
            {t('account.orderDetail.title')}
          </h1>
          <p className="account-order-detail__uuid">{uuid}</p>
        </div>
        <div className="account-order-detail__meta">
          <StatusBadge
            code={orderStatusCode}
            label={t(`account.orderStatus.${orderStatusCode}`)}
          />
          {createdAt && (
            <span className="account-order-detail__date">
              {formatDateTime(createdAt, 'long')}
            </span>
          )}
          {orderStatusCode === EOrderStatus.CREATED && (
            <div className="account-order-detail__actions">
              {onPayNow && (
                <button
                  type="button"
                  className="account-order-detail__pay-now"
                  onClick={onPayNow}
                  disabled={isRetryingPayment || isCancellingOrder}
                >
                  {isRetryingPayment
                    ? t('account.orderDetail.payNowLoading')
                    : t('account.orderDetail.payNow')}
                </button>
              )}
              {onCancelOrder && (
                <button
                  type="button"
                  className="account-order-detail__cancel"
                  onClick={onCancelOrder}
                  disabled={isRetryingPayment || isCancellingOrder}
                >
                  {isCancellingOrder
                    ? t('account.orderDetail.cancelLoading')
                    : t('account.orderDetail.cancel')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {paymentRetryError && (
        <p className="account-order-detail__pay-error">
          {t('account.orderDetail.payNowError')}
        </p>
      )}

      {orderCancelError && (
        <p className="account-order-detail__pay-error">
          {t('account.orderDetail.cancelError')}
        </p>
      )}

      <div className="account-order-detail__body">
        <section className="account-order-detail__products">
          <h2>{t('account.orderDetail.products')}</h2>
          <table className="account-order-detail__table">
            <thead>
              <tr>
                <th>{t('basket.productName')}</th>
                <th>{t('basket.count')}</th>
                <th>{t('basket.pricePerUnit')}</th>
                <th>{t('basket.totalPrice')}</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.uuid}>
                  <td>
                    <Link
                      to={product.link}
                      onClick={(): void => {
                        dispatch(
                          load({
                            product: {
                              uuid: product.uuid,
                            },
                          }),
                        );
                      }}
                    >
                      {product.name}
                    </Link>
                  </td>
                  <td>{product.count}</td>
                  <td>{formatPrice(product.pricePerUnit, currencyIsoCode)}</td>
                  <td>{formatPrice(product.lineTotal, currencyIsoCode)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="account-order-detail__totals">
            {deliveryFee !== undefined && deliveryFee > 0 && (
              <div className="account-order-detail__totals-row">
                <span>{t('account.orderDetail.delivery')}</span>
                <span>{formatPrice(deliveryFee, currencyIsoCode)}</span>
              </div>
            )}
            <div className="account-order-detail__totals-row account-order-detail__totals-row--total">
              <span>{t('order.totalPrice')}</span>
              <span>{formatPrice(totalPrice, currencyIsoCode)}</span>
            </div>
            {companyHasPricesWithoutTax && (
              <p className="account-order-detail__vat-note">
                {t('account.orders.priceExclTax')}
              </p>
            )}
          </div>

          {hasInvoice && invoiceUrl && (
            <a
              href={invoiceUrl}
              download
              className="account-order-detail__invoice-link"
            >
              {t('account.orders.downloadInvoice')}
            </a>
          )}
        </section>

        <div className="account-order-detail__addresses">
          {billingAddress && (
            <section className="account-order-detail__address-card">
              <h2>{t('basket.billingAddress')}</h2>
              <address>
                <p>
                  {billingAddress.name} {billingAddress.surname}
                </p>
                {billingAddress.companyName && (
                  <p>{billingAddress.companyName}</p>
                )}
                <p>
                  {billingAddress.street}
                  {billingAddress.streetNumber
                    ? ` ${billingAddress.streetNumber}`
                    : ''}
                </p>
                <p>
                  {billingAddress.zip} {billingAddress.city}
                </p>
                {billingAddress.countryLabel && (
                  <p>{billingAddress.countryLabel}</p>
                )}
                {billingAddress.email && <p>{billingAddress.email}</p>}
              </address>
            </section>
          )}

          {deliveryAddress && (
            <section className="account-order-detail__address-card">
              <h2>{t('basket.deliveryAddress')}</h2>
              <address>
                <p>
                  {deliveryAddress.name} {deliveryAddress.surname}
                </p>
                <p>
                  {deliveryAddress.street}
                  {deliveryAddress.streetNumber
                    ? ` ${deliveryAddress.streetNumber}`
                    : ''}
                </p>
                <p>
                  {deliveryAddress.zip} {deliveryAddress.city}
                </p>
                {deliveryAddress.countryLabel && (
                  <p>{deliveryAddress.countryLabel}</p>
                )}
              </address>
            </section>
          )}
        </div>

        {(paymentMethodName || deliveryMethodName) && (
          <div className="account-order-detail__methods">
            {paymentMethodName && (
              <p>
                {t('order.paymentMethod')}: {paymentMethodName}
              </p>
            )}
            {deliveryMethodName && (
              <p>
                {t('order.deliveryMethod')}: {deliveryMethodName}
              </p>
            )}
          </div>
        )}
      </div>

      {backUrl && (
        <div className="account-order-detail__back">
          <Link to={backUrl}>
            {'← '}
            {backLabel ?? t('account.navigation.orders')}
          </Link>
        </div>
      )}
    </div>
  );
}
